import { useCallback, useEffect, useRef, useState } from 'react';
import {
    fetchExerciseAvailability,
    fetchExercisePermissionStatus,
    fetchExerciseSessions,
    requestExercisePermission,
} from '../bridge/healthBridge';
import { syncWatchExercise } from '../api/exerciseAPI';
import { getUsrInfoId } from '../utils/user';

/** 워치(삼성 헬스) 운동 기록 동기화 */

const BACKFILL_DAYS = 30;
const REFRESH_DAYS = 7;

/** 연동 가능 상태 */
export const EXERCISE_ACCESS = Object.freeze({
    CHECKING: 'CHECKING',
    NOT_NATIVE: 'NOT_NATIVE',
    UNSUPPORTED: 'UNSUPPORTED',
    NOT_INSTALLED: 'NOT_INSTALLED',
    NEED_PERMISSION: 'NEED_PERMISSION',
    READY: 'READY',
});

const backfillKey = () => `iitp.exercise.backfilled.${getUsrInfoId()}`;

let syncInFlight = null;

const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const daysAgo = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - n);
    return d;
};

/** 삼성 헬스 기록 1건 → 서버 저장 형식 */
const toSaveItem = (s) => {
    const start = new Date(s.startTime);
    const end = s.endTime ? new Date(s.endTime) : null;
    const stamp = (d) =>
        `${toLocalDate(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;

    return {
        exrcRecId: s.id,
        exrcTyCd: s.exerciseType,
        exrcNm: s.title || null,
        recDe: `${start.getFullYear()}${pad(start.getMonth() + 1)}${pad(start.getDate())}`,
        recTm: `${pad(start.getHours())}${pad(start.getMinutes())}${pad(start.getSeconds())}`,
        exrcBeginTm: stamp(start),
        exrcEndTm: end ? stamp(end) : null,
        exrcMin: s.minutes,
        cnsumCalor: s.calories,
        exrcDstncVal: s.distance,
        dstncUnit: s.distance == null ? null : 'm',
        exrcOrglData: JSON.stringify(s),
    };
};

export function useWatchExerciseSync(onSynced) {
    const aliveRef = useRef(true);
    const onSyncedRef = useRef(onSynced);
    onSyncedRef.current = onSynced;

    const [access, setAccess] = useState(EXERCISE_ACCESS.CHECKING);

    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    /** 동의 화면 없이 현재 상태만 확인 */
    const checkAccess = useCallback(async () => {
        let availability;
        try {
            availability = await fetchExerciseAvailability();
        } catch (e) {
            return e?.code === 'NOT_NATIVE'
                ? EXERCISE_ACCESS.NOT_NATIVE
                : EXERCISE_ACCESS.UNSUPPORTED;
        }

        if (!availability?.available) {
            return availability.reason === 'APP_NOT_INSTALLED'
                ? EXERCISE_ACCESS.NOT_INSTALLED
                : EXERCISE_ACCESS.UNSUPPORTED;
        }

        try {
            const { granted } = await fetchExercisePermissionStatus();
            return granted ? EXERCISE_ACCESS.READY : EXERCISE_ACCESS.NEED_PERMISSION;
        } catch (e) {
            return EXERCISE_ACCESS.UNSUPPORTED;
        }
    }, []);

    /** 삼성 헬스에서 읽어 서버 저장. 새로 저장한 건수 반환 */
    const pull = useCallback(async () => {
        const backfilled = localStorage.getItem(backfillKey()) === '1';
        const spanDays = backfilled ? REFRESH_DAYS : BACKFILL_DAYS;

        const sessions = await fetchExerciseSessions(
            toLocalDate(daysAgo(spanDays - 1)),
            toLocalDate(daysAgo(0)),
        );

        const usable = (sessions || []).filter((s) => s.id);
        const dropped = (sessions?.length ?? 0) - usable.length;
        if (dropped > 0) console.warn(`[exercise] uid 없는 기록 ${dropped}건 제외`);

        // 0건이면 소급 완료로 치지 않는다. 워치 동기화가 아직 안 끝났을 수 있다
        if (usable.length === 0) return 0;

        const savedCnt = await syncWatchExercise(usable.map(toSaveItem));

        if (!backfilled) localStorage.setItem(backfillKey(), '1');

        return savedCnt;
    }, []);

    const sync = useCallback(async () => {
        if (!syncInFlight) {
            syncInFlight = pull().finally(() => {
                syncInFlight = null;
            });
        }

        try {
            const savedCnt = await syncInFlight;
            if (aliveRef.current && savedCnt > 0) onSyncedRef.current?.(savedCnt);
        } catch (e) {
            console.warn('[exercise] 워치 기록 동기화 실패 —', e?.message);
        }
    }, [pull]);

    /** 상태 재확인 + 가능하면 동기화 */
    const refresh = useCallback(async () => {
        const next = await checkAccess();
        if (!aliveRef.current) return next;
        setAccess(next);
        if (next === EXERCISE_ACCESS.READY) sync();
        return next;
    }, [checkAccess, sync]);

    /** 삼성 헬스 동의 화면 표시. 화면의 안내 모달에서만 호출 */
    const askPermission = useCallback(async () => {
        let granted = false;
        try {
            granted = !!(await requestExercisePermission())?.granted;
        } catch (e) {
            console.warn('[exercise] 운동 권한 요청 실패 —', e?.message);
        }
        if (!aliveRef.current) return granted;
        setAccess(granted ? EXERCISE_ACCESS.READY : EXERCISE_ACCESS.NEED_PERMISSION);
        if (granted) sync();
        return granted;
    }, [sync]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // 앱 복귀 시 재조회
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState === 'visible') refresh();
        };
        document.addEventListener('visibilitychange', onVisible);
        return () => document.removeEventListener('visibilitychange', onVisible);
    }, [refresh]);

    return { access, askPermission, sync, refresh };
}
